import { useEffect, useMemo, useRef, useState } from 'react'
import { Dropdown } from '@/components/ui/Dropdown'
import { useListboxNav } from '@/hooks/useListboxNav'
import { motion } from '@/utils/motion'
import { cn } from '@/utils/cn'
import { focusRing } from '@/components/ui/buttonStyles'
import { ADMIN_SECTIONS, ADMIN_SECTION_GROUPS, findAdminSection } from './adminSections'
import type { AdminTab } from '@/types/admin'

interface AdminSectionPickerProps {
  active: AdminTab
  onSelect: (key: AdminTab) => void
}

/**
 * The section picker for screens too narrow for the sidebar.
 *
 * It replaces a native `<select>`: the OS picker cannot show a section's description,
 * cannot mark the current one in the app's own idiom, and arrives styled by the phone
 * rather than by Mímir. This is the app's own `Dropdown` — a bottom sheet on a phone,
 * an anchored panel above that — carrying a grouped listbox, with the rows dealt in
 * on open through `motion` (anime.js, ADR-013) so the panel assembles itself instead
 * of appearing all at once.
 */
export function AdminSectionPicker({ active, onSelect }: AdminSectionPickerProps) {
  const [open, setOpen] = useState(false)
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([])
  const current = findAdminSection(active)
  const currentIndex = useMemo(() => ADMIN_SECTIONS.findIndex((s) => s.key === active), [active])

  function choose(index: number) {
    const section = ADMIN_SECTIONS[index]
    if (!section) return
    setOpen(false)
    onSelect(section.key)
  }

  const { activeIndex, setActiveIndex, handleKeyDown } = useListboxNav(ADMIN_SECTIONS.length, {
    onSelect: choose,
    onClose: () => setOpen(false),
    open,
  })

  // Opens on the section you are already in, rather than on the first one. Runs after
  // the hook's own reset-to-zero, which is registered earlier in the component.
  useEffect(() => {
    if (open && currentIndex >= 0) setActiveIndex(currentIndex)
  }, [open, currentIndex, setActiveIndex])

  useEffect(() => {
    if (open) optionRefs.current[activeIndex]?.focus()
  }, [open, activeIndex])

  // Deals the rows in each time the panel opens.
  useEffect(() => {
    if (!open) return
    const rows = optionRefs.current.filter((el): el is HTMLButtonElement => el !== null)
    void motion.staggerIn(rows, { step: 26, ms: 300 })
  }, [open])

  return (
    <div className="lg:hidden">
      <Dropdown
        open={open}
        onOpenChange={setOpen}
        trigger={
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-label={`Admin section: ${current.label}`}
            className={cn(
              'flex min-h-11 w-full items-center justify-between gap-3 rounded-md',
              'border border-border-divider bg-surface-bg px-4 py-3',
              'transition-colors duration-200 hover:border-accent/40',
              focusRing,
            )}
          >
            <span className="min-w-0 text-left">
              <span className="block font-mono text-[0.65rem] uppercase tracking-widest text-muted">
                {groupOf(active)}
              </span>
              <span className="block truncate font-serif text-base text-heading">
                {current.label}
              </span>
            </span>
            <Chevron open={open} />
          </button>
        }
      >
        <div
          role="listbox"
          aria-label="Admin sections"
          aria-activedescendant={
            ADMIN_SECTIONS[activeIndex] ? `admin-section-${ADMIN_SECTIONS[activeIndex].key}` : undefined
          }
          onKeyDown={handleKeyDown}
          tabIndex={-1}
          className="max-h-[70vh] w-full overflow-y-auto py-2 outline-none sm:w-[min(22rem,calc(100vw-2rem))]"
        >
          {ADMIN_SECTION_GROUPS.map((group, g) => (
            <div key={group.label}>
              {g > 0 && <div role="presentation" className="mx-4 my-2 border-t border-line-soft" />}
              <p
                role="presentation"
                className="px-4 pt-2 pb-1 font-mono text-[10px] uppercase tracking-[0.28em] text-muted"
              >
                {group.label}
              </p>
              {group.sections.map((section) => {
                const index = ADMIN_SECTIONS.indexOf(section)
                const selected = section.key === active
                return (
                  <button
                    key={section.key}
                    id={`admin-section-${section.key}`}
                    ref={(el) => {
                      optionRefs.current[index] = el
                    }}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    tabIndex={-1}
                    onClick={() => choose(index)}
                    onMouseEnter={() => setActiveIndex(index)}
                    className={cn(
                      'flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors duration-150 focus:outline-none',
                      index === activeIndex && 'bg-surface-bg/60',
                      selected ? 'text-accent' : 'text-body hover:text-accent',
                    )}
                  >
                    <span className="flex-1 font-serif text-sm">{section.label}</span>
                    {selected && (
                      <span aria-hidden="true" className="text-xs text-accent">
                        ✓
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          ))}
        </div>
      </Dropdown>

      <p className="mt-2 font-serif text-sm text-muted">{current.description}</p>
    </div>
  )
}

function groupOf(key: AdminTab): string {
  return ADMIN_SECTION_GROUPS.find((g) => g.sections.some((s) => s.key === key))?.label ?? 'Panel'
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={cn('shrink-0 text-muted transition-transform duration-200', open && 'rotate-180')}
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  )
}
